/**
 * What the preparing page tells the operator to do next. Pure and React-free so
 * it is unit-tested.
 *
 * The preparing phases were designed as transient — `prepare.start` runs merge,
 * capture and describe back to back, so the page was only ever expected to be
 * seen at `reviewing`. When any of those three does not land, the operator is
 * looking at a page with no primary action at all: v1.1264 sat in `capturing`
 * for an hour whose only enabled button was "Re-run describe" in a collapsed
 * accordion, which was the wrong step. So the page derives its call to action
 * from the step statuses the host publishes, and always has exactly one.
 *
 * The step statuses rather than the phase, because they carry `failed`, which
 * the phase cannot: a failed capture and an unstarted one are the same phase and
 * want the same button, but they do not want the same wording above it.
 *
 * The phase is consulted for one thing the steps cannot express: whether the host
 * that runs them is up. Milestones re-derived from git do not know, so a release
 * parked at the review gate with its host stopped reports review as due — and the
 * page then pointed at an editor whose every button the legality table had
 * disabled. Starting the host is the action in that phase, so it outranks them.
 */
import {
    PREPARE_PHASE_GUIDANCE,
    type HostDoc,
    type PhaseGuidance,
    type Phase,
    type PrepareReleaseDoc,
    type PrepareStepName,
    type StepStatus,
    type UnreleasedDoc,
} from '@finvari/deployer';

/**
 * What the page should put in front of the operator:
 *  - `start-host`, when the machine that runs every other action is not up;
 *  - one of the automated steps, when that step is the one waiting or has failed;
 *  - `review`, when the three are done and the announcement is the action;
 *  - `undefined`, when there is nothing to offer (published, or every step done).
 */
export type NextPrepareAction = 'start-host' | 'merge' | 'capture' | 'describe' | 'review' | undefined;

/**
 * The steps the page can offer as its next action, in order. `review` is the
 * human gate and `publish`/`mergeBack` are reached through approval rather than
 * offered as steps, so the automated three are what a next-step card runs.
 */
const OFFERABLE: Array<Exclude<NextPrepareAction, 'start-host' | 'review' | undefined>> = [
    'merge',
    'capture',
    'describe',
];

export function nextPrepareStep(
    steps: Record<PrepareStepName, StepStatus> | undefined,
    phase?: Phase,
): NextPrepareAction {
    if (phase === 'prepare_host_starting') return 'start-host';
    if (!steps) return undefined;
    // A failed step is offered in place of the step after it: the host stopped
    // there, so nothing later can be what is actually due, whatever its own
    // status says.
    const stuck = OFFERABLE.find((s) => steps[s] === 'failed');
    if (stuck) return stuck;
    const waiting = OFFERABLE.find((s) => steps[s] === 'next');
    if (waiting) return waiting;
    if (steps.review === 'next' || steps.review === 'failed') return 'review';
    return undefined;
}

/** Whether the action the page offers is the operator's own work, not a step to run. */
export function isReviewAction(next: NextPrepareAction): next is 'review' {
    return next === 'review';
}

/**
 * The phase translated into "what is happening / what you do now". A lookup with
 * a name, so callers do not reach into the record and so a phase the record
 * somehow lacks degrades to the phase name rather than blank space.
 */
export function phaseGuidance(phase: Phase): PhaseGuidance {
    return PREPARE_PHASE_GUIDANCE[phase] ?? { headline: phase, detail: '' };
}

/** What the preparing page knows about commits that landed on main since the merge. */
export interface NewMainCommits {
    /** Commits on main the release branch does not have, as of the last check. */
    commits: number;
    /** There are some, and the check is current enough to say so. */
    ahead: boolean;
    /**
     * The check describes a release branch the host has since moved off, so it
     * cannot answer either way — the page offers a re-check rather than a merge.
     */
    stale: boolean;
    /** No check has ever run on this checkout. */
    unchecked: boolean;
}

/**
 * Whether work has landed on main since the release branch was merged.
 *
 * Read from `commitsAheadOfRelease` and not from `commits`, which is measured
 * from the deploy baseline and therefore counts the whole release — during a
 * preparation it is always positive and would offer "include new commits"
 * forever.
 *
 * Staleness matters more here than on the idle page: the merge step moves the
 * release branch, so every successful preparation step invalidates the last
 * check. An operator acting on a pre-merge check would be told main is ahead by
 * the commits their own merge just took in.
 */
export function newCommitsOnMain(doc: UnreleasedDoc | undefined, prepareHost: HostDoc | undefined): NewMainCommits {
    if (!doc) return { commits: 0, ahead: false, stale: false, unchecked: true };
    const stale = !!prepareHost?.checkoutHead && !!doc.releaseSha && prepareHost.checkoutHead !== doc.releaseSha;
    const commits = doc.commitsAheadOfRelease ?? 0;
    return { commits, ahead: !stale && commits > 0, stale, unchecked: false };
}

/** The fields the "What is shipping" caption counts. */
type CapturedCounts = Pick<
    PrepareReleaseDoc,
    'prFileCount' | 'directCommits' | 'pendingAppCount' | 'pendingConnectorCount'
>;

/**
 * The one-line count above the change set: what capture found, and what it
 * deliberately left for later.
 *
 * Extracted from the view because every field it reads is one a release
 * document may not have. They arrived at different times — `directCommits` well
 * after the first releases — and the page shows whichever release is newest,
 * which on an idle system is an old one. Reading `directCommits.length` straight
 * off the document took the whole preparing page down with a TypeError, so the
 * counting lives here where a test can hold it to tolerating an absent field.
 */
export function capturedSummary(release: CapturedCounts): string {
    const parts = [`${release.prFileCount ?? 0} server PRs captured`];
    const direct = release.directCommits?.length ?? 0;
    if (direct > 0) parts.push(`${direct} direct commits (no PR)`);
    if (release.pendingAppCount > 0) parts.push(`${release.pendingAppCount} app PRs filed for later`);
    if (release.pendingConnectorCount > 0) {
        parts.push(`${release.pendingConnectorCount} connector PRs filed for later`);
    }
    return parts.join(' · ');
}
